import { useContext } from "react";

import { BaseCompanyLogo } from "../../../../components/BaseComponents/BaseCompanyLogo";
import BaseGridRatingHorizontal from "../../../../components/BaseComponents/BaseGridRatingHorizontal";
import BaseLinkWrapper from "../../../../components/BaseComponents/BaseLinkWrapper";
import { BaseVisitLink } from "../../../../components/BaseComponents/BaseVisitLink";
import { CompanyContext } from "../../../../components/CompanyList/CompanyContext";
import {
	useFoldPlacement,
	useFoldPlacementCompanies,
} from "../../../../components/FoldPlacements/useFoldPlacements";
import { BaseCompanies } from "../../../../components/UIComponents/BaseCompanies";
import { DiscountIcon } from "../../../assets/icons/DiscountIcon";

const BUSINESS_BANKING_FOLD_PLACEMENTS = {
	experimentKey: "prod-business-banking-fold-placements",
	placements: [
		{
			bannerText: "Best For SMBs",
			settingsKey: "fold_placement_highlight_left",
		},
		{
			bannerText: "Best For Cash Flow",
			settingsKey: "fold_placement_highlight_right",
		},
	],
	ctaText: "View Rates",
};

export const formatFoldPlacementApyText = (interestRate) => {
	const [, rate, footnote] =
		String(interestRate)
			.trim()
			.match(/^(\d*\.?\d+)(.*)$/) ?? [];

	return Number(rate) > 0 ? `Earn Up to ${rate}%${footnote} APY` : null;
};

export const BusinessBankingFoldPlacements = () => {
	const companies = useFoldPlacementCompanies(BUSINESS_BANKING_FOLD_PLACEMENTS);

	if (companies.length === 0) {
		return null;
	}

	return (
		<div className="tw:flex tw:w-full tw:justify-center tw:gap-[8px] tw:pt-[5px] tw:xs:gap-[16px] tw:desktop:gap-[30px] tw:desktop:py-[14px]">
			<BaseCompanies
				companies={companies}
				sliceStart={0}
				count={companies.length}
			>
				<BusinessBankingFoldPlacementCard />
			</BaseCompanies>
		</div>
	);
};

const BusinessBankingFoldPlacementCard = () => {
	const { company } = useContext(CompanyContext);
	const { bannerText, highlightText } = useFoldPlacement(
		BUSINESS_BANKING_FOLD_PLACEMENTS.placements,
	);
	const apyText =
		highlightText ?? formatFoldPlacementApyText(company.fields.interestRate);

	return (
		<BaseLinkWrapper
			className="tw:group tw:flex tw:min-w-0 tw:flex-1 tw:flex-col tw:rounded-[3px] tw:bg-white"
			extraQueryParams={{ plc: "above_list_header" }}
		>
			<div className="tw:relative tw:flex tw:flex-col tw:desktop:h-[32px] tw:desktop:flex-row tw:desktop:items-center tw:desktop:justify-between tw:desktop:rounded-t-[3px] tw:desktop:bg-[#EDF4FE] tw:desktop:pr-[20px]">
				<p className="tw:relative tw:z-1 tw:m-0 tw:flex tw:h-[32px] tw:items-center tw:justify-center tw:rounded-t-[3px] tw:bg-[#333742] tw:pt-[2px] tw:pb-[4px] tw:font-avenirNextDemi tw:text-[12px] tw:leading-[16px] tw:text-white tw:[clip-path:polygon(0_0,100%_0,100%_calc(100%-4px),calc(50%+4px)_calc(100%-4px),50%_100%,calc(50%-4px)_calc(100%-4px),0_calc(100%-4px))] tw:xs:text-[14px] tw:desktop:rounded-tr-none tw:desktop:pt-[3px] tw:desktop:pr-[27px] tw:desktop:pb-0 tw:desktop:pl-[20px] tw:desktop:text-[14px] tw:desktop:[clip-path:polygon(0_0,100%_0,calc(100%-12px)_50%,100%_100%,0_100%)]">
					{bannerText}
				</p>
				{apyText && (
					<div className="tw:mt-[-4px] tw:flex tw:h-[28px] tw:items-center tw:justify-center tw:gap-[8px] tw:bg-[#EDF4FE] tw:pt-[2px] tw:desktop:mt-0 tw:desktop:h-auto tw:desktop:bg-transparent tw:desktop:pt-0">
						<p className="tw:m-0 tw:pt-[3px] tw:font-avenirNextDemi tw:text-[12px] tw:leading-[16px] tw:text-[#333742] tw:xs:text-[14px] tw:desktop:text-right">
							{apyText}
						</p>
						<DiscountIcon className="tw:hidden tw:text-[24px] tw:text-[#333742] tw:desktop:inline-block" />
					</div>
				)}
			</div>

			<div className="tw:flex tw:flex-col tw:gap-[16px] tw:px-[8px] tw:py-[16px] tw:min-[360px]:px-[16px] tw:xs:p-[20px] tw:desktop:flex-row tw:desktop:items-center tw:desktop:justify-between tw:desktop:p-[20px]">
				<div className="tw:flex tw:flex-col tw:items-center tw:gap-[8px] tw:desktop:w-[204px] tw:desktop:items-start">
					<BaseCompanyLogo
						classNames={{
							picture:
								"tw:block tw:h-[40px] tw:w-full tw:xs:h-[56px] tw:desktop:h-[64px]",
							image: "tw:size-full tw:object-contain",
						}}
					/>

					<BaseGridRatingHorizontal
						classNames={{
							wrapper:
								"tw:flex tw:items-center tw:gap-[3px] tw:min-[360px]:gap-[5px] tw:xs:gap-[7px]",
							score:
								"tw:m-0 tw:font-avenirNextBold tw:text-[13px] tw:text-[#1E293B] tw:leading-[12px] tw:tracking-[-0.39px] tw:xs:text-[20px] tw:xs:leading-[20px] tw:xs:tracking-[-0.6px] tw:desktop:w-[37px] tw:desktop:text-[#333742] tw:desktop:text-[24px] tw:desktop:leading-[24px] tw:desktop:tracking-[-0.96px]",
							stars:
								"tw:flex tw:items-center tw:gap-[3px] tw:min-[360px]:gap-[6px] tw:desktop:gap-[8px]",
							starColor: "tw:text-[#00B679]",
							starSize:
								"tw:gap-[1px] tw:text-[10px] tw:xs:text-[14px] tw:desktop:translate-y-[-1px] tw:desktop:text-[16px]",
							count:
								"tw:m-0 tw:font-avenirNextDemi tw:text-[10px] tw:leading-[11px] tw:text-[#1E293B] tw:xs:text-[14px] tw:xs:leading-[16px] tw:desktop:text-[16px] tw:desktop:text-[#333742] tw:desktop:leading-[24px]",
						}}
					/>
				</div>

				<div className="tw:flex tw:h-[40px] tw:w-full tw:items-center tw:justify-center tw:rounded-[3px] tw:bg-[#00B679] tw:transition-all tw:group-hover:bg-[#008F66] tw:xs:h-[48px] tw:desktop:h-[56px] tw:desktop:w-[200px]">
					<BaseVisitLink
						wrapper="tw:flex tw:items-center tw:gap-[8px] tw:after:hidden tw:after:mx-[2px] tw:after:size-[16px] tw:after:bg-[url(/public/react-images/common/newTab.svg)] tw:after:bg-cover tw:after:bg-no-repeat tw:xs:after:block tw:desktop:after:size-[20px]"
						contentWrapper="tw:pt-[3px] tw:font-avenirNextDemi tw:text-[14px] tw:leading-[22px] tw:text-[#F4F7F8] tw:xs:text-[16px] tw:desktop:text-[18px]"
					>
						{BUSINESS_BANKING_FOLD_PLACEMENTS.ctaText}
					</BaseVisitLink>
				</div>
			</div>
		</BaseLinkWrapper>
	);
};
